import { DoNotDisturbOnOutlined, InfoRounded, VisibilityOffOutlined } from '@mui/icons-material'
import GpsFixedIcon from '@mui/icons-material/GpsFixed'
import GpsNotFixedIcon from '@mui/icons-material/GpsNotFixed'
import { Box, MenuItem, Select, Stack, Tooltip, Typography, useTheme } from '@mui/material'
import { GridColumns } from '@mui/x-data-grid-pro'
import { t } from 'i18next'
import React, { memo, useMemo } from 'react'
import { toast } from 'react-toastify'
import { invalidateComponentsCache } from 'src/api/components'
import { invalidateMooringMapCaches } from 'src/api/moorings'
import { IPaginatedResult } from 'src/api/paginated-result'
import { Spinner } from 'src/components/Spinner'
import { CageConfig } from 'src/constant'
import { BottomFasten, ComponentPlacementType, Sediment } from 'src/enums'
import { MUIThemeColors } from 'src/mui-theme'
import { Flex } from 'src/styles/flexComponent'
import { getThemeColor } from 'src/utils'
import { getCageName } from 'src/utils/function'
import { invalidatePositionCache, useUpdatePosition } from '../../../../api/positions'
import { GpsToDms } from '../../../../components/GpsToDms'
import { IPositionType, IPositionWithDrawing } from '../../../../generated-types'
import { IPositionDrawing } from '../../../../generated-types/position-drawing'
import {
  AssignTemplates,
  CreatePosition,
  GenerateComponents,
  ManageTemplates,
  UploadOlex,
} from './PositionTableToolbarOptionsHelper'
import { DynamicTextTypography } from 'src/components/DynamicTextTypography'

interface RenderCellLonLatProps {
  latitude: any
  longitude: any
  textOrientation?: string
}

export const RenderCellLonLat: React.FC<RenderCellLonLatProps> = memo(
  ({ latitude, longitude, textOrientation = 'flex-col' }) => {
    return (
      <>
        {latitude || longitude ? (
          <Stack>
            <Box display={textOrientation} gap={2}>
              {latitude ? (
                <Stack direction={'row'}>
                  <GpsFixedIcon className="pt-1 pr-1" fontSize="inherit" color="primary" />
                  <GpsToDms decimalGPS={latitude} isLongitude={false} />
                </Stack>
              ) : (
                ''
              )}
              {longitude ? (
                <Stack direction={'row'}>
                  <GpsNotFixedIcon className="pt-1 pr-1" fontSize="inherit" color="primary" />
                  <GpsToDms decimalGPS={longitude} isLongitude={true} />
                </Stack>
              ) : (
                ''
              )}
            </Box>
          </Stack>
        ) : (
          '/'
        )}
      </>
    )
  },
)

export const PositionsTableStaticColumns: GridColumns = [
  {
    field: 'name',
    headerName: t('position', { ns: 'position' }),
    flex: 1,
    sortComparator: (v1, v2) => {
      const n1 = Number(v1)
      const n2 = Number(v2)
      if (!isNaN(n1) && !isNaN(n2)) return n1 - n2
      return String(v1 ?? '').localeCompare(String(v2 ?? ''))
    },
    renderCell: params => {
      const name =
        params.row.name >= CageConfig.OFFSET_START && CageConfig.OFFSET_END
          ? getCageName(params.row.name) ?? 'N/A'
          : params.row.name
      const markers: JSX.Element[] = []
      if (params.row.isHidden) {
        markers.push(
          <Tooltip key="hidden" title={t('hidden', { ns: 'common' }) as string}>
            <VisibilityOffOutlined fontSize="small" color="disabled" />
          </Tooltip>,
        )
      }
      if (params.row.isActive === false) {
        markers.push(
          <Tooltip key="inactive" title={t('inactive', { ns: 'common' }) as string}>
            <DoNotDisturbOnOutlined fontSize="small" color="disabled" />
          </Tooltip>,
        )
      }

      return markers.length > 0 ? (
        <Stack direction="row" gap={0.5} alignItems="center">
          {markers}
          <span>{name}</span>
        </Stack>
      ) : (
        name
      )
    },
  },
  {
    field: 'reference',
    headerName: t('reference', { ns: 'position' }),
    flex: 1,
    renderCell: params => params.row.reference ?? 'N/A',
  },
  {
    field: 'latitude',
    headerName: t('latitude', { ns: 'common' }) + '/' + t('longitude', { ns: 'common' }),
    flex: 1.8,
    renderCell: params => <RenderCellLonLat latitude={params.row.latitude} longitude={params.row.longitude} />,
  },
  {
    field: 'mbl',
    headerName: t('mbl', { ns: 'position' }),
    flex: 1,
    renderCell: params => <Box sx={{ ml: -1 }}>{params.row.mbl ?? 'N/A'}</Box>,
  },
  {
    field: 'accidentLoad',
    headerName: t('accidentLoad', { ns: 'position' }),
    flex: 1.4,
    renderCell: params => params.row.accidentLoad ?? 'N/A',
  },
  {
    field: 'depth',
    headerName: t('depth', { ns: 'position' }),
    flex: 1,
    renderCell: params => params.row.depth ?? 'N/A',
  },
  {
    field: 'direction',
    headerName: t('direction', { ns: 'position' }),
    flex: 1,
    renderCell: params => params.row.direction ?? 'N/A',
  },
  {
    field: 'testLoad',
    headerName: t('testLoad', { ns: 'position' }),
    flex: 1.5,
    renderCell: params => params.row.testLoad ?? 'N/A',
  },
  {
    field: 'bottomFasten',
    headerName: t('bottomAttachment', { ns: 'position' }),
    flex: 1.4,
    renderCell: params =>
      params.row.bottomFasten != null ? t(BottomFasten[params.row.bottomFasten], { ns: 'position' }) : 'N/A',
  },
  {
    field: 'sediment',
    headerName: t('sediment', { ns: 'position' }),
    flex: 1.5,
    renderCell: value => {
      return Sediment[value.value] ?? 'N/A'
    },
  },
]

export const PositionsTableToolbarOptions = ({
  customerId,
  localityId,
  mooringId,
  mooringType,
  selectedIds,
  positions,
  selectedPositionTypes,
}) => [
  {
    label: t('add-template', { ns: 'mooring' }),
    Component: (
      <ManageTemplates
        selectedIds={selectedIds}
        mooringId={mooringId}
        positions={positions}
        customerId={customerId}
        selectedPositionTypes={selectedPositionTypes}
      />
    ),
  },
  {
    label: t('addPosition', { ns: 'position' }),
    Component: <CreatePosition customerId={customerId} localityId={localityId} mooringId={mooringId} />,
  },
  {
    label: t('upload-olex', { ns: 'mooring' }),
    Component: (
      <UploadOlex customerId={customerId} localityId={localityId} mooringId={mooringId} mooringType={mooringType} />
    ),
    showSeparator: true,
  },
  {
    label: t('generateComponents', { ns: 'mooring' }),
    Component: (
      <GenerateComponents
        customerId={customerId}
        localityId={localityId}
        mooringId={mooringId}
        selectedIds={selectedIds}
        selectedPositionTypes={selectedPositionTypes}
      />
    ),
  },
  {
    label: t('assign-template', { ns: 'mooring' }),
    Component: (
      <AssignTemplates
        customerId={customerId}
        localityId={localityId}
        mooringId={mooringId}
        selectedIds={selectedIds}
        positions={positions}
        selectedPositionTypes={selectedPositionTypes}
      />
    ),
  },
]

interface PositionsTableTemplateRenderProps {
  position: IPositionWithDrawing
  customerId: EntityId
  localityId: EntityId
  mooringId: EntityId
  availablePositionDrawings?: IPaginatedResult<IPositionDrawing>
  isLoading?: boolean
  componentPlacementTabType?: ComponentPlacementType
}

export const PositionsTableTemplateRender: React.FC<PositionsTableTemplateRenderProps> = memo(
  ({
    position,
    customerId,
    localityId,
    mooringId,
    availablePositionDrawings,
    isLoading,
    componentPlacementTabType,
  }) => {
    const { mutateAsync: mutationUpdate } = useUpdatePosition(
      customerId,
      localityId,
      mooringId,
      position?.id as EntityId,
    )
    const theme = useTheme()
    const positionDrawings = useMemo(() => {
      const positionDrawings = [
        ...(availablePositionDrawings?.items ?? []).filter(x => x.category == position.type),
        { id: -1, drawingNumber: t('n/a', { ns: 'common' }) } as IPositionDrawing,
      ]

      const currentDrawingId =
        componentPlacementTabType === ComponentPlacementType.Sunk
          ? position.sunkPositionDrawingId
          : position.positionDrawingId

      const currentDrawingExists = positionDrawings?.find(x => x.id === currentDrawingId)

      if (currentDrawingId && !currentDrawingExists) {
        const currentDrawing =
          componentPlacementTabType === ComponentPlacementType.Sunk
            ? position.sunkPositionDrawing
            : position.positionDrawing

        if (currentDrawing) {
          positionDrawings.push(currentDrawing as IPositionDrawing)
        }
      }

      return positionDrawings
    }, [
      availablePositionDrawings?.items,
      position.type,
      componentPlacementTabType,
      position.positionDrawingId,
      position.sunkPositionDrawingId,
      position.positionDrawing,
      position.sunkPositionDrawing,
    ])

    const value =
      (componentPlacementTabType === ComponentPlacementType.Sunk
        ? positionDrawings?.find(x => x.id == position.sunkPositionDrawingId)?.id
        : positionDrawings?.find(x => x.id == position.positionDrawingId)?.id) ?? -1

    const handleTemplateChange = ({ value, position }) => {
      const selectedPositionDrawing = positionDrawings?.find(x => x.id == value.props.value)
      const positionDrawingProducts = selectedPositionDrawing?.positionDrawingProducts
      if (positionDrawingProducts?.length == 0) {
        toast(t('noProductsAssignedInTheTemplate', { ns: 'mooring' }), { type: 'error' })
        return
      }
      const positionDrawingId = value.props.value == -1 ? null : value.props.value
      if (componentPlacementTabType === ComponentPlacementType.Sunk) position.sunkPositionDrawingId = positionDrawingId
      else position.positionDrawingId = positionDrawingId
      position.regenerateComponents = false
      position.regenerateSunkComponents = false

      mutationUpdate(position, {
        onSuccess: () => {
          toast(t('templateAssignedSuccessfully', { ns: 'mooring' }), { type: 'success' })
          invalidatePositionCache.getPositionsWithMinimalData(
            customerId as EntityId,
            localityId as EntityId,
            mooringId as EntityId,
          )
          invalidateComponentsCache.getComponents(customerId as EntityId, position.id as EntityId)
          invalidateMooringMapCaches(customerId, localityId, mooringId)
          invalidatePositionCache.getPosition(
            customerId as EntityId,
            localityId as EntityId,
            mooringId as EntityId,
            position?.id as EntityId,
          )
        },
      })
    }

    if (isLoading) return <Spinner size={'24px'} />

    if (positionDrawings?.length == 1)
      return (
        <Flex.Row gap={1}>
          <Tooltip title={`${t('info-manage-template', { ns: 'position' })}`}>
            <InfoRounded sx={{ color: getThemeColor(theme, MUIThemeColors.primaryMain) }} />
          </Tooltip>
          <Typography variant="body2">{t('no-assigned-templates', { ns: 'mooring' })}</Typography>
        </Flex.Row>
      )

    return (
      position && (
        <Box>
          <Select
            sx={{ width: 150, height: 35 }}
            value={value}
            onChange={(_, value) => handleTemplateChange({ value, position })}
            disabled={position.type === IPositionType.Cage}
            renderValue={selected => {
              const drawing = positionDrawings?.find(d => d.id === selected)
              return (
                <DynamicTextTypography text={drawing?.drawingNumber ?? t('n/a', { ns: 'common' })} maxLength={12} />
              )
            }}
          >
            {positionDrawings && positionDrawings.length > 1 ? (
              positionDrawings.map(positionDrawing => (
                <MenuItem key={positionDrawing.id} value={positionDrawing.id}>
                  {positionDrawing.drawingNumber}
                </MenuItem>
              ))
            ) : (
              <MenuItem key={-1} value={-1}>
                <Typography variant="body2">{t('n/a', { ns: 'common' })}</Typography>
              </MenuItem>
            )}
          </Select>
        </Box>
      )
    )
  },
)

export default PositionsTableTemplateRender
